import { api } from '../api.js';
import { getCurrentUser } from '../auth.js';
import { effectiveDeadline, expiredLabel, isPrefilterRejected } from './tenders-format.js';

export {
  DOC_KIND_LABEL, PREFILTER_SOURCE_LABEL, WORK_SCOPE_LABEL, eisUrl, expiredLabel, meltorUrl, fmtBytes, fmtDate,
  fmtDeadline, isPrefilterRejected, prefilterBadge,
} from './tenders-format.js';

/**
 * Общее для раздела «Аукционы»: подписи статусов, словарь переходов (копия
 * TRANSITIONS из backend/app/api/tenders.py — держать в синхроне), форматирование.
 */

export const STATUS_LABEL = {
  new:              'Новая',
  screened:         'Разложена',
  decision:         'На решении',
  approved:         'Участвуем',
  rejected:         'Отказ',
  docs:             'Документы',
  submitted:        'Подана',
  control:          'Контроль',
  won:              'Выиграли',
  lost:             'Проиграли',
  customer_refused: 'Отказ заказчика',
  guarantee:        'Гарантия',
  contract:         'Контракт',
  cancelled:        'Отменена',
  expired:          'Просрочен',
};

/** Основная цепочка (колонки kanban до «Закрытых»). */
export const FLOW = ['new', 'screened', 'decision', 'approved', 'docs', 'submitted', 'won', 'guarantee', 'contract'];
export const FINAL = new Set(['contract', 'lost', 'customer_refused', 'rejected', 'cancelled', 'expired']);
/** Закрытые, кроме contract (у него своя колонка). */
export const CLOSED_OTHER = ['rejected', 'lost', 'customer_refused', 'cancelled'];

const BASE = {
  new:       ['screened'],
  screened:  ['decision'],
  decision:  ['approved', 'rejected'],
  approved:  ['docs'],
  docs:      ['submitted'],
  // ТЕНД-ДИНАР-3: из «Подана» на «Контроль» (и обратно); control — не линейный, в FLOW не входит.
  submitted: ['control', 'won', 'lost', 'customer_refused'],
  control:   ['submitted', 'won', 'lost', 'customer_refused'],
  won:       ['guarantee'],
  guarantee: ['contract'],
};

export const TRANSITIONS = Object.fromEntries(
  Object.keys(STATUS_LABEL).map(s => [s, new Set(BASE[s] ? [...BASE[s], 'cancelled'] : [])]),
);
// expired ставит housekeeping (worker); вернуть в работу может только owner.
TRANSITIONS.expired = new Set(['new']);
export const OWNER_ONLY = new Set(['expired>new']);

/**
 * «Разложена» и «На решении» ставит только раннер раскладки (Х-1): вручную эти шаги не
 * делаются ни в kanban, ни кнопками карточки (UI-2).
 */
export const AUTO_ONLY = new Set(['new>screened', 'screened>decision']);
export const manualAllowed = (from, to) => !AUTO_ONLY.has(`${from}>${to}`);

/** Отбор, обогащение и запуск раскладки видит только владелец (UI-2, п.8). */
export const isOwner = () => getCurrentUser()?.role === 'owner';

/** «№59 (0373100128326000096)» — как карточку называют уведомления, дайджест и отчёты. */
export const cardName = (t) => (t?.reg_number ? `№${t.id} (${t.reg_number})` : `№${t?.id}`);

/** Переход разрешён словарём и ролью (expired → new — только owner). */
export const roleAllows = (from, to, role) => !OWNER_ONLY.has(`${from}>${to}`) || role === 'owner';

/**
 * Подпись статуса для бейджа: у expired — причина («Просрочен» / «Ушёл из выгрузки»),
 * отсеянные префильтром — «Отсеяна», ТЕНД-ДИНАР-3: cancelled по инициативе заказчика —
 * «Отменён заказчиком» (обычная отмена — «Отменена»).
 */
export const statusText = (t) => expiredLabel(t)
  ?? (isPrefilterRejected(t) ? 'Отсеяна' : null)
  ?? (t.status === 'cancelled' && t.closed_reason === 'customer_cancelled' ? 'Отменён заказчиком' : null)
  ?? STATUS_LABEL[t.status] ?? t.status;

/** approved/rejected ставятся только через POST /{id}/decision. */
export const DECISION_TARGETS = new Set(['approved', 'rejected']);

export const canTransition = (from, to) => TRANSITIONS[from]?.has(to) ?? false;

// incomplete — раскладка без текста документов (A2); неизвестные значения показываются как есть.
export const RECOMMENDATION_LABEL = {
  go: 'Участвовать', no_go: 'Не участвовать', borderline: 'На грани', incomplete: 'Неполная (нет документов)',
};
export const DECISION_LABEL = { go: 'Участвуем', no_go: 'Отказ' };

/** Этапы ведения (tender_stages.stage). doneAt — статус, с которого этап считается пройденным. */
export const STAGES = [
  { key: 'docs',           label: 'Документы',           doneAt: 'submitted', date: null },
  { key: 'submission',     label: 'Подача заявки',       doneAt: 'submitted', date: 'submission_deadline' },
  { key: 'auction',        label: 'Торги',               doneAt: 'won',       date: 'auction_date' },
  { key: 'bank_guarantee', label: 'Банковская гарантия', doneAt: 'contract',  date: null },
  { key: 'contract',       label: 'Контракт',            doneAt: 'contract',  date: null },
];

/** Позиция статуса в основной цепочке (-1 для закрытых вне цепочки). */
export const flowIndex = (status) => FLOW.indexOf(status);

const money0 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

export function fmtMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${money0.format(n)} ₽` : '—';
}

export function fmtNum(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '';
}

/**
 * Ц-0: проверка цены участия перед «Участвуем». Цену всегда вводит человек, рекомендованная —
 * только подсказка. Возвращает:
 *   { error }  — пусто, не число или ≤ 0 (запрос не отправлять);
 *   { price, confirm } — price как число; confirm непуст, если цена похожа на ошибку в
 *                        порядке цифр и нужно подтверждение «Ты уверен?».
 * Подозрение (любое из): цена больше НМЦК; есть рекомендованная и цена отличается от неё
 * в 3 раза и больше в любую сторону (нестрого, без деления на 0); рекомендованной нет и
 * цена меньше 10 % НМЦК. Условия с НМЦК пропускаются, если НМЦК нет.
 */
export function checkPrice1(raw, { recommended, nmck } = {}) {
  if (raw == null || String(raw).trim() === '') return { error: 'Укажите цену участия' };
  const price = Number(raw);
  if (!Number.isFinite(price) || price <= 0) return { error: 'Укажите цену участия' };
  const rec = Number(recommended);
  const hasRec = recommended != null && recommended !== '' && Number.isFinite(rec) && rec > 0;
  const n = Number(nmck);
  const hasNmck = nmck != null && nmck !== '' && Number.isFinite(n) && n > 0;
  const suspicious =
    (hasNmck && price > n)
    || (hasRec && (price >= rec * 3 || rec >= price * 3))
    || (!hasRec && hasNmck && price < n * 0.1);
  const confirm = suspicious
    ? `Ты уверен? Цена ${fmtMoney(price)}`
      + (hasRec ? `, рекомендуемая ${fmtMoney(rec)}` : '')
      + (hasNmck ? `, НМЦК ${fmtMoney(n)}` : '')
    : '';
  return { price, confirm };
}

const DAY_MS = 86_400_000;

/** Подача закрывается в ближайшие `days` дней (и ещё не закрылась). */
export function deadlineSoon(t, days = 2, now = Date.now()) {
  if (!t.submission_deadline || t.is_closed) return false;
  const d = effectiveDeadline(t)?.getTime();
  if (d == null) return false;
  return d >= now && d <= now + days * DAY_MS;
}

export function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Текст ошибки API для человека. apiFetch сводит объект detail в JSON-строку,
 * бэкенд аукционов шлёт {error, detail, allowed} на 409 — достаём detail.
 */
export function apiErrorText(e) {
  let d = apiErrorDetail(e);
  if (d && typeof d === 'object') d = d.detail ?? d.message ?? d.error ?? JSON.stringify(d);
  return d || e?.message || 'Ошибка';
}

/** detail ошибки API: объект ({error, message, files} у 422 загрузки) или строка. */
export function apiErrorDetail(e) {
  const d = e?.detail;
  if (typeof d === 'string' && d.startsWith('{')) {
    try { return JSON.parse(d); } catch { /* строка как есть */ }
  }
  return d;
}

/**
 * Для Dashboard: «ждут решения», «подача ≤ 2 дней» и «на проверку Руслану» (префильтр review)
 * одним запросом GET /api/tenders/facets по открытым карточкам.
 */
export async function tendersSummary() {
  const f = await api.tenders.facets({ is_closed: false });
  return { decision: f.status.decision ?? 0, soon: f.deadline_soon ?? 0, review: f.prefilter.review ?? 0 };
}
